<template>
  <quillEditor
    v-if="isPcTerminal"
    v-model:value="innerValue"
    :options="mergedEditorOption"
    v-bind="attrs"
    ref="editorRef"
    @ready="onEditorReady"
  />
  <a-textarea
    v-else
    v-model:value="innerValue"
    :placeholder="placeholder"
    :rows="rows"
    v-bind="attrs"
  />
</template>

<script setup>
import { computed, useAttrs, ref, onMounted, onBeforeUnmount } from 'vue'
import { quillEditor } from 'vue3-quill'

const props = defineProps({
  isQuillEditor: {
    type: Boolean,
    default: false
  },
  value: {
    type: [String, Number],
    default: ''
  },
  terminalId: {
    type: [String, Number],
    default: ''
  },
  placeholder: {
    type: String,
    default: '请输入'
  },
  rows: {
    type: Number,
    default: 4
  },
  editorOption: {
    type: Object,
    default: () => ({})
  },
  preventInitAutoFocus: {
    type: Boolean,
    default: true
  },
  scrollIntoViewClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:value'])
const attrs = useAttrs()

const editorRef = ref(null)
const editorRootRef = ref(null)
const lastUserIntentAt = ref(0)
const USER_INTENT_THRESHOLD = 500

const defaultEditorOption = {
  placeholder: '请输入',
  modules: {
    toolbar: [
      [{ font: ['Muli-Light', 'Note-Regular'] }],
      [
        {
          size: [
            '12px',
            '14px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '26px',
            '28px',
            '30px',
            '32px',
            '34px',
            '36px',
            '38px',
            '40px'
          ]
        }
      ],
      ['bold', 'link'],
      [
        {
          color: [
            'red',
            'blue',
            'green',
            'black',
            'yellow',
            'pink',
            'white',
            '#14AA6E',
            '#999999'
          ]
        }
      ]
    ]
  }
}

const mergedEditorOption = computed(() => ({
  ...defaultEditorOption,
  ...props.editorOption
}))

const isPcTerminal = computed(() => String(props.terminalId) === '1')

const innerValue = computed({
  get() {
    return props.value
  },
  set(val) {
    emit('update:value', val)
  }
})

const markUserIntent = () => {
  lastUserIntentAt.value = Date.now()
}

const isRecentUserIntent = () =>
  Date.now() - lastUserIntentAt.value <= USER_INTENT_THRESHOLD

const getClassSelector = className => {
  if (!className) return ''
  const normalized = String(className).trim()
  if (!normalized) return ''
  if (normalized.startsWith('.')) return normalized
  return `.${normalized.split(/\s+/).join('.')}`
}

const scrollToTargetByClass = () => {
  const selector = getClassSelector(props.scrollIntoViewClass)
  if (!selector) return
  const target = document.querySelector(selector)
  if (!target) return
  target.scrollIntoView({
    behavior: 'auto',
    block: 'nearest',
    inline: 'nearest'
  })
}

const preventUnexpectedFocus = () => {
  if (!props.preventInitAutoFocus || !editorRootRef.value) return

  if (isRecentUserIntent()) return

  requestAnimationFrame(() => {
    editorRootRef.value?.blur()
    scrollToTargetByClass()
  })
}

const onEditorReady = quill => {
  editorRef.value = quill
  const rootEl = quill?.root
  if (!rootEl) return

  if (editorRootRef.value && editorRootRef.value !== rootEl) {
    editorRootRef.value.removeEventListener('focus', preventUnexpectedFocus)
  }

  editorRootRef.value = rootEl
  editorRootRef.value.addEventListener('focus', preventUnexpectedFocus)
  preventUnexpectedFocus()
}

onMounted(() => {
  document.addEventListener('pointerdown', markUserIntent, true)
  document.addEventListener('keydown', markUserIntent, true)
  document.addEventListener('wheel', markUserIntent, true)
  document.addEventListener('touchstart', markUserIntent, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', markUserIntent, true)
  document.removeEventListener('keydown', markUserIntent, true)
  document.removeEventListener('wheel', markUserIntent, true)
  document.removeEventListener('touchstart', markUserIntent, true)
  if (editorRootRef.value) {
    editorRootRef.value.removeEventListener('focus', preventUnexpectedFocus)
  }
})
</script>
